import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {ChevronDown,ChevronLeft,ChevronRight,Map,Target,Award,BookOpen,} from "lucide-react";

const PassportResultModal = ({id, onClose,}: {id?: string; onClose: () => void;}) => {
	const API_URL = import.meta.env.VITE_API_URL;
    const navigate = useNavigate();
	const queryClient = useQueryClient();

	const [selectedLevel, setSelectedLevel] = useState(1);
	const [passportReulstList, setPassportReulstList] = useState<any[]>([]);
	const [openedCard, setOpenedCard] = useState<string | null>(null);

	const { data } = useQuery({
		queryKey: ['passportResultList', id],
		queryFn: async () => {
			const params = new URLSearchParams();
			params.append("childId", id);

			const response = await fetch(`${API_URL}/bbs/ajax.get_passport_result_list.php`, {
				method: "POST",
				headers: {
					"Content-Type": "application/x-www-form-urlencoded",
				},
				body: params,
				credentials: "include",
			});

			if (!response.ok) {
				throw new Error("네트워크 오류");
			}

			return response.json();
		},
		enabled: !!id,
	});

	const mutation = useMutation({
		mutationFn: async (level: string) => {
			const params = new URLSearchParams();
			params.append("childId", id);
			params.append("exploreLevel", level);

			const response = await fetch(`${API_URL}/bbs/ajax.get_passport_result_list.php`, {
					method: "POST",
					headers: {
						"Content-Type": "application/x-www-form-urlencoded",
					},
					body: params,
					credentials: "include",
				}
			);

			if (!response.ok) {
				throw new Error("네트워크 오류");
			}

			return response.json();
		},
		onSuccess: (response) => {
			if (!response.success) {
				switch (response.code) {
					case "INVALID_REQUEST":
						alert("필수 필드값이 입력되지 않았습니다.");
						break;
					default:
						alert("알 수 없는 오류가 발생했습니다.");
						break;
				}
				return;
			}
			setPassportReulstList(response.data?.list ?? []);
		},
		onError: (error: any) => {
			console.log(error.message);
		},
	});

	const handleLevelChange = (level: number) => {
		if (level < 1 || level > 3) return;
		if (level === selectedLevel) return;
		if (mutation.isPending) return;
		setSelectedLevel(level);
		mutation.mutate(level);
	};

	const groupedPassportResult = Object.values(
		passportReulstList.reduce((acc: any, item: any) => {
			const key = item.exploreCode;

			if (!acc[key]) {
				acc[key] = {
					exploreCode: item.exploreCode,
					exploreLevel: item.exploreLevel,
					exploreName: item.exploreName,
					missions: [],
					badges: [],
				};
			}

			if (item.resultType === "mission") {
				acc[key].missions.push(item);
			}

			if (item.resultType === "badge") {
				acc[key].badges.push(item);
			}

			return acc;
		}, {})
	);

	useEffect(() => {
		if (data?.data?.list) {
			setPassportReulstList(data.data.list);
			if (data.data.list[0]?.exploreLevel) {
				setSelectedLevel(
					Number(data.data.list[0].exploreLevel)
				);
			}
		}
	}, [data]);

	useEffect(() => {
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = "unset";
		};
	}, []);

	const missions = {
        IMG : {1:'🎨',2:'🖼️',3:'🎭'},
        VID : {1:'🎬',2:'🎥',3:'📹'},
        STY : {1:'📖',2:'📚',3:'📜'},
        MUS : {1:'🎵',2:'🎼',3:'🎹'},
        WRT : {1:'✍️',2:'🖊️',3:'✒️'},
        MTH : {1:'🔢',2:'➗',3:'∑'},
        WND : {1:'🔬',2:'🧪',3:'⚗️'},
    };

    const badges = [
        {id: 'speed', icon:'🏃', name:'스피드러너', desc:'한 달 안에 한 나라 완주'},
        {id: 'perfect', icon:'🎯', name:'완벽주의자', desc:'모든 프로젝트 100점'},
        {id: 'presenter', icon:'🎤', name:'발표왕', desc:'프로젝트 10번 발표'},
        {id: 'passion', icon:'🔥', name:'열정왕', desc:'3일 연속 미션 완수'}
    ];

    return (
    	<div className="fixed inset-0 bg-black/70 z-[1000] flex items-center justify-center p-3 md:p-6">
    		<div className="relative w-full max-w-4xl h-[80vh] bg-white rounded-[28px] overflow-hidden shadow-2xl flex flex-col">
    			<div className="sticky top-0 z-20 bg-white border-b px-4 md:px-8 py-5 flex items-center justify-between flex-wrap gap-2">
    				<div>
    					<h2 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2">
    						🌍 탐험 기록
    					</h2>
    					<p className="text-sm md:text-base text-gray-500 mt-1">
    						수행한 미션과 획득한 배지를 확인해보세요
    					</p>
    				</div>
    				<div className="flex items-center gap-2">
    					<button
    						onClick={() =>
    						handleLevelChange(selectedLevel - 1)
    					}
    					className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 text-lg font-bold transition disabled:opacity-40"
    					disabled={
    						selectedLevel === 1 ||
    						mutation.isPending
    					}
	    				>
	    					←
	    				</button>
	    				<div className="px-4 py-2 rounded-full bg-purple-100 text-purple-700 font-bold text-sm md:text-base whitespace-nowrap">
	    					LEVEL {selectedLevel}
	    				</div>
	    				<button
	    					onClick={() =>
	    					handleLevelChange(selectedLevel + 1)
	    				}
	    				className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 text-lg font-bold transition disabled:opacity-40"
	    				disabled={
	    					selectedLevel === 3 ||
	    					mutation.isPending
	    				}
	    			>
	    				→
	    			</button>
	    		</div>
    		</div>
    		{mutation.isPending && (
    			<div className="flex-1 overflow-y-auto px-4 md:px-8 py-5 bg-gradient-to-b from-slate-50 to-white">
	    			<div className="flex justify-center items-center py-24">
	    				<div className="w-12 h-12 rounded-full border-4 border-purple-200 border-t-purple-600 animate-spin"></div>
	    			</div>
	    		</div>
    		)}
    		{!mutation.isPending &&
    		passportReulstList?.length === 0 && (
    			<div className="flex-1 overflow-y-auto px-4 md:px-8 py-5 bg-gradient-to-b from-slate-50 to-white">
	    			<div className="h-full flex flex-col items-center justify-center text-center py-20">
	    				<div className="text-7xl mb-4">🧳</div>
	    				<h3 className="text-2xl font-bold text-gray-700">
	    					탐험 기록이 없어요
	    				</h3>
	    				<p className="text-gray-500 mt-2">
	    					새로운 탐험을 시작해보세요!
	    				</p>
	    			</div>
	    		</div>
    		)}
			<div className="flex-1 min-h-0 overflow-y-auto bg-gradient-to-b from-slate-50 to-white">
				{groupedPassportResult?.map((item: any, index: number) => {
					const isOpen = openedCard === item.exploreCode;
					return (
						<div
							key={index}
							className="bg-white overflow-hidden shadow-sm hover:shadow-md transition-all duration-200"
						>
							<div
								onClick={() => setOpenedCard(isOpen ? null : item.exploreCode)}
								className="cursor-pointer px-4 md:px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
							>
								<div className="flex items-center gap-3 min-w-0">
									<div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-100 to-fuchsia-100 flex items-center justify-center text-2xl shrink-0 border border-violet-200">
										{missions[item.exploreCode as keyof typeof missions]?.[item.exploreLevel as	| 1	| 2	| 3]}
									</div>
									<div className="min-w-0">
										<div className="flex items-center gap-2 flex-wrap">
											<h3 className="text-base md:text-lg font-bold text-gray-900 truncate">
												{item.exploreName}
											</h3>
											<div className="px-2 py-1 rounded-full bg-violet-100 text-violet-700 text-[11px] font-bold">
												LV.{item.exploreLevel}
											</div>
										</div>
										<div className="flex items-center gap-2 mt-2 flex-wrap">
											<div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-50 text-blue-600 text-xs font-semibold">
												<Target size={13} />
												<span>
													미션 {item.missions.length}
												</span>
											</div>
											<div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold">
												<Award size={13} />
												<span>
													배지 {item.badges.length}
												</span>
											</div>
										</div>
									</div>
								</div>
								<div
									className={`transition-transform duration-200 shrink-0 ${
										isOpen ? "rotate-180" : ""
									}`}
								>
									<ChevronDown size={22} className="text-gray-400"/>
								</div>
							</div>
							{isOpen && (
								<div className="border-t bg-gray-50 px-4 md:px-5 py-4 space-y-5">
									<div>
										<div className="flex items-center gap-2 mb-3">
											<div className="w-8 h-8 rounded-xl bg-blue-100 flex items-center justify-center">
												<Target	size={16} className="text-blue-600"/>
											</div>
											<h4 className="font-bold text-gray-800">
												수행 미션
											</h4>
										</div>
										{item.missions.length === 0 ? (
											<div className="text-sm text-gray-400 px-1">
												완료한 미션이 없어요
											</div>
										) : (
											<div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
												{item.missions.map((mission: any, idx: number) => (
													<div key={idx} className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl border border-emerald-200 px-3 py-3 flex items-start gap-3 shadow-sm"
													>
														<div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
															<span className="text-lg">🎯</span>
														</div>
														<div className="min-w-0 flex-1">
															<div className="flex items-center gap-2 mb-1">
																<span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold">
																	COMPLETE
																</span>
															</div>
															<p className="font-semibold text-sm text-emerald-900 leading-relaxed break-words">
																{mission.exploreMission}
															</p>
														</div>
													</div>
												))}
											</div>
										)}
									</div>
									<div>
										<div className="flex items-center gap-2 mb-3">
											<div className="w-8 h-8 rounded-xl bg-amber-100 flex items-center justify-center">
												<Award size={16} className="text-amber-600" />
											</div>
											<h4 className="font-bold text-gray-800">
												획득 배지
											</h4>
										</div>
										{item.badges.length === 0 ? (
											<div className="text-sm text-gray-400 px-1">
												획득한 배지가 없어요
											</div>
										) : (
											<div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
												{item.badges.map((badge: any, idx: number) => {
													const badgeInfo = badges.find((b: any) => b.id === badge.id);
													return (
														<div key={idx} className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl border border-yellow-200 px-4 py-4 flex items-start gap-3 shadow-sm">
															<div className="w-11 h-11 rounded-2xl bg-white flex items-center justify-center text-2xl shrink-0 shadow-sm border border-yellow-100">
																{badgeInfo?.icon ?? "🏆"}
															</div>
															<div className="min-w-0 flex-1">
																<div className="flex items-center gap-2 flex-wrap">
																	<p className="font-bold text-sm text-gray-900">
																		{badgeInfo?.name ?? badge.id}
																	</p>
																	<span className="px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 text-[10px] font-bold">
																		BADGE
																	</span>
																</div>
																<p className="text-xs text-gray-500 mt-1 leading-relaxed break-words">
																	{badgeInfo?.desc ?? "특별 업적을 달성했어요"}
																</p>
															</div>
														</div>
													);
												})}
											</div>
										)}
									</div>
								</div>
							)}
						</div>
					);
				})}
			</div>
	    	<div className="border-t bg-white p-4 md:p-5 flex gap-3">
	    		<button
	    			type="button"
	    			onClick={onClose}
	    			className="w-full px-4 py-3 rounded-2xl text-sm md:text-base font-semibold bg-gray-200 hover:bg-gray-300 transition"
	    		>
	    			닫기
	    		</button>

	    		<button
	    			type="button"
	    			onClick={() => navigate("/passport")}
	    			className="w-full px-4 py-3 rounded-2xl text-sm md:text-base font-semibold bg-purple-600 text-white hover:bg-purple-700 transition"
	    		>
	    			탐험하러가기
	    		</button>
	    	</div>
	    </div>
	</div>
	);
}

export default PassportResultModal;